import styled, { css, DefaultTheme } from 'styled-components';
import { StyledFileRadioList } from 'components/styledComponents/radio/Radio.styled';
import { getBoxShadow, media, truncate } from 'utils/styledHelpers';
import { FormInputWrapper } from 'components/styledComponents/input/Input.styled';
import { StyledButtonBlue } from 'components/styledComponents/button/Button.styled';
import { StyledTruncate } from 'shared/Common.styled';
import { StyledFlag, StyledSvgIcon } from 'shared/icon/Icon.styled';

type Colors = keyof DefaultTheme['colors'];

interface StyledComposeTemplateWrapperProps {
  $noShadow?: boolean;
}
interface StyledComposeTemplateRightProps {
  $isPlayer?: boolean;
  $isHeight?: boolean;
}
interface StyledMessageSubjectProps {
  $noSubject?: boolean;
  $isColumn?: boolean;
  $borderTop?: boolean;
  $noPaddingBottom?: boolean;
}
interface StyledPreviewMessageSubjectProps {
  $alignLeft?: boolean;
  $isRegular?: boolean;
}
interface ComposeTextWrapperProps {
  $noContent?: boolean;
}
interface TemplateCardProps {
  $isSelected?: boolean;
}

interface PopupWrapperForSaveActionProps {
  $smallWidth?: boolean;
}
interface PreviewTotalInfoWrapperProps {
  $noBorder?: boolean;
}
interface PreviewContentTitleProps {
  $isRegular?: boolean;
}
interface RecipientsCustomTableStyleProps {
  $viewAll?: boolean;
}

interface ConversationListStylesProps {
  $isSenderUser?: boolean;
  $isMeRecalled?: boolean;
}
interface ConversationMessageContentProps {
  $isMeRecalled?: boolean;
}
interface ConversationContentProps {
  $isMeRecalled?: boolean;
}
interface StyledTruncateFileNameProps {
  $isPreview?: boolean;
}
interface StyledContentPaddingsProps {
  $isPaddingTop?: boolean;
  $isFullHeight?: boolean;
}
interface StyledPushPreviewItemProps {
  $isApple?: boolean;
  $isExpanded?: boolean;
}
interface StyledPushPreviewTitleProps {
  $isExpanded?: boolean;
}
interface StyledPushPreviewContentProps {
  $isExpanded?: boolean;
  $isApple?: boolean;
}
interface StyledPushExpandedAttachmentProps {
  $isExpanded?: boolean;
  $isApple?: boolean;
}
interface StyledChannelsIconsWrapperProps {
  $length?: boolean;
}
interface StyledNotifyPlayersCountProps {
  $color: Colors;
}
interface StyledSelectedTemplateNameProps {
  $isInvalid?: boolean;
}

const StyledComposeTemplateWrapper = styled.div<StyledComposeTemplateWrapperProps>`
  position: relative;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  box-shadow: ${(props) => (props.$noShadow ? 'none' : `${getBoxShadow(props.theme.colors.secondaryShadow)}`)};
  flex-grow: 1;
  width: 100%;
  .ant-row {
    height: 100%;
    .ant-col-16 {
      max-height: 100%;
      overflow-y: auto;
    }
    .ant-col {
      ${media.sm`
        position: static;   
      `}
    }
  }
  .ant-tabs {
    border-radius: 0;
  }
`;

const StyledComposeTemplateLeft = styled.div`
  height: 100%;
  border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  box-shadow: 5px 0 6px -2px rgb(75 75 75 / 10%);
  .ant-form-item-label > label {
    color: ${(props) => props.theme.colors.primaryText};
  }
  .ant-tabs > .ant-tabs-nav .ant-tabs-nav-list {
    border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  }
  .ant-collapse {
    border: none;
    border-radius: initial;
    width: 100%;
    background: transparent;
    position: relative;
    .ant-collapse-item {
      padding: 1.4rem 0;
      border-radius: 0 !important;
      border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
      background: transparent;
      &-active {
        padding-bottom: 0;
      }
      .ant-collapse-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 0 4rem;
        position: relative;
        @media (max-width: 1366px) {
          padding: 0 0 0 2.3rem;
        }
        span {
          margin: 0 0 0 1.79rem;
        }
        .ant-collapse-title {
          display: flex;
          align-items: center;
          margin: 0;
        }
        .ant-collapse-expand-icon {
          position: absolute;
          display: flex;
          top: 50%;
          transform: translateY(-50%);
          padding-top: 0;
          left: 2rem;
          @media (max-width: 1366px) {
            left: 1rem;
          }
        }
      }
      &:first-of-type {
        .ant-collapse-content {
          overflow: visible;
        }
      }
      &-disabled {
        opacity: 0.6;
      }
    }
  }

  .ant-collapse-panel {
    background-color: initial;
    border-top: none;
    .ant-spin-nested-loading .ant-table-container {
      background: transparent;
    }
    .ant-table .ant-table-tbody > tr:hover,
    .ant-table-tbody > tr.ant-table-row:hover > td {
      background: transparent !important;
    }
    .ant-form-item-children-icon {
      display: none;
    }
    .ant-select-arrow {
      right: 1.14rem;
    }
    > .ant-collapse-body {
      padding: 1.71rem 2rem 1.71rem;
      @media (max-width: 1366px) {
        padding: 1.43rem 1.2rem 1.71rem;
      }
      .ant-form-item:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const StyledComposeTemplateRight = styled.div<StyledComposeTemplateRightProps>`
  ${(props) => props.$isPlayer
    && `
    &:before {
      content: "";
      display: block;
      width: 100%;
      height: 4rem;
      margin-bottom: 2.84rem;
      border-bottom: 1px solid ${props.theme.colors.secondaryBorder};
    }
  `};

  .ant-tabs > .ant-tabs-nav {
    height: ${(props) => (props.$isHeight ? '4rem' : '100%')};
  }
  .ant-tabs:not(.ant-tabs-card) {
    width: 100%;
    height: 100%;
    > .ant-tabs-content-holder {
      padding: 0 4rem;
      margin-top: 1.7rem;

      ${media.sm`
        padding: 0 1.4rem;
      `}

      .ant-tabs-tabpane {
        position: relative;
      }
    }
  }
  .ant-tabs-bar {
    margin-bottom: 0;
  }
  .ant-tabs-tab {
    padding: 1.1rem 2rem;
    margin: 0;
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    text-transform: uppercase;
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    position: relative;
    border-bottom: none;
    &-active {
      &:before {
        position: absolute;
        bottom: 0;
        left: 0;
        content: '';
        width: 100%;
        border-bottom: 2px solid ${(props) => props.theme.colors.blue};
      }
    }
  }
  > button {
    position: absolute;
    top: 1.43rem;
    right: 2.29rem;
  }
  .content-overlay {
    background: transparent;
  }
`;
const StyledComposeInnerTabsWrapper = styled.div`
  .ant-tabs {
    margin-bottom: 1.29rem;

    .ant-tabs-nav {
      margin: 0;
      height: 2.86rem;

      .ant-tabs-tab {
        padding: 0.25rem 1.14rem;
        font-weight: ${(props) => props.theme.typography.fontWeightRegular};
        width: 12.8rem;
        justify-content: center;
        border-color: ${(props) => props.theme.colors.secondaryBorder};
        border-radius: ${(props) => `${props.theme.customBorderRadius.borderRadiusSmall} ${props.theme.customBorderRadius.borderRadiusSmall} 0 0`};

        &-active {
          border-bottom-color: ${(props) => props.theme.colors.secondaryBg};

          &:before {
            display: none;
          }
        }
      }

      & > .ant-tabs-nav .ant-tabs-tab:not(.ant-tabs-tab-active),
      .ant-tabs-card > div > .ant-tabs-nav .ant-tabs-tab {
        background: ${(props) => props.theme.colors.grayButton};
      }

      .ant-tabs-nav-wrap {
        margin: 0;
      }
    }

    .tox-editor-container {
      .tox-tbtn {
        &[aria-label='Undo'],
        &[aria-label='Redo'] {
          border: 1px solid ${(props) => props.theme.colors.primaryBorder};
          border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusDefault};
          width: 2rem;
          height: 2rem;
          margin-left: 0.5rem;
        }
      }
      .tox-mbtn {
        span {
          font-size: ${(props) => props.theme.typography.fontSizeDefault};
          color: ${(props) => props.theme.colors.primaryText};
        }
      }
      .tox-statusbar {
        display: none;
      }
    }

    .ant-upload.ant-upload-drag {
      display: inline-flex;
      align-self: flex-start;
      width: auto;
      height: 2rem;
      margin: 0.57rem 0 0;
      border: none;
      background-color: ${(props) => props.theme.colors.grayButton};
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusLarge};

      .ant-upload {
        padding: 0 1.14rem;
        height: 100%;
        line-height: 1.71rem;
      }
      &:hover {
        background-color: ${(props) => props.theme.colors.secondaryBorder};
        span {
          color: ${(props) => props.theme.colors.blue};
        }
      }
    }
  }
`;
const StyledComposeTemplateMargin = styled(StyledComposeTemplateRight)`
  .ant-tabs-nav-wrap {
    margin-left: 4.2rem;
  }
  .ant-tabs-content-holder .ant-tabs-nav-wrap {
    margin-left: 0;
  }
  &:before {
    content: none;
  }
`;
const MessageSwitcherWrapper = styled.div`
  padding-bottom: 2rem;
  span {
    margin-right: 1.29rem;
  }
`;
const StyledComposeButtons = styled.div`
  position: absolute;
  right: 2.29rem;
  top: 0.85rem;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  ${media.sm`
    right: 1.71rem;
  `}
`;

const StyledMessageSubject = styled.div<StyledMessageSubjectProps>`
  position: relative;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  padding: ${(props) => (props.$noSubject ? '0.53rem' : '1.71rem')};
  padding-bottom: ${(props) => (props.$noPaddingBottom ? 0 : '')};
  display: flex;
  flex-direction: ${(props) => (props.$isColumn ? 'column' : 'row')};
  gap: 1rem;
  align-items: ${(props) => (props.$isColumn ? 'flex-start' : 'center')};

  ${(props) => props.$borderTop
    && `
    border-top: ${props.$borderTop ? `1px solid ${props.theme.colors.secondaryBorder}` : 'none'};
    border-radius: ${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle} 0 0;
  `}

  &:before,
  &:after {
    content: '';
    position: absolute;
    height: 10px;
    width: 1px;
    top: 100%;
    background-color: ${(props) => props.theme.colors.secondaryBorder};
  }
  &:before {
    left: -1px;
  }
  &:after {
    right: -1px;
  }

  span {
    color: ${(props) => props.theme.colors.primaryText};
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    flex: 0 0 auto;

    &:first-of-type {
      font-weight: ${(props) => props.theme.typography.fontWeightBold};
    }
  }

  ${FormInputWrapper} {
    width: 100%;
    input {
      font-size: ${(props) => props.theme.typography.fontSizeDefault};
    }
  }
`;
const PreviewMessageSubjectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-top: none;
  padding: 1.21rem 1.71rem 1.71rem;
  min-height: 43rem;
  overflow-y: auto;
  border-radius: ${(props) => `0 0 ${props.theme.customBorderRadius.borderRadiusSmall} ${props.theme.customBorderRadius.borderRadiusSmall}`};
  .tox.tox-tinymce {
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  }
`;
const StyledPreviewMessageSubject = styled.span<StyledPreviewMessageSubjectProps>`
  width: 100%;
  background: ${(props) => props.theme.colors.primaryBg};
  color: ${(props) => props.theme.colors.primaryText};
  padding: 1.14rem;
  text-align: ${(props) => (props.$alignLeft ? 'left' : 'center')};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-weight: ${(props) => (props.$isRegular ? props.theme.typography.fontWeightRegular : props.theme.typography.fontWeightMedium)};
  font-size: ${(props) => props.theme.typography.fontSizeMiddle};
  display: inline-block;
  word-break: break-word;
  margin-bottom: 0.71rem;
  span {
    opacity: 60%;
  }
`;
const CreateMessageContentWrapper = styled.div`
  .tox .tox-tbtn--select {
    margin-left: 0.5rem;
  }
`;
const ComposeTextWrapper = styled.div<ComposeTextWrapperProps>`
  display: flex;
  align-items: center;
  word-break: break-all;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  margin-top: 0.57rem;
  justify-content: center;
  height: ${(props) => (props.$noContent ? '40rem' : '90%')};
  flex-direction: column;
  padding: 1.14rem;
  > * {
    max-width: 46.86rem;
    text-align: center;
    margin: 0 auto;
    > img {
      margin-bottom: 3.86rem;
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    }
  }
`;
const StyledSelectedPlayers = styled.div`
  text-transform: initial;
  margin-left: auto;
  color: ${(props) => props.theme.colors.filterLabelColor};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  height: 1.14rem;
  @media (max-width: 1366px) {
    padding-right: 1rem;
  }
  text-align: right;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
`;

const StyledRecipientsWrapper = styled.div`
  & .ant-tabs-nav {
    width: 100%;
    margin-bottom: 1.36rem;
    &-list {
      display: flex;
      flex: 0 0 100%;
      .ant-tabs-tab {
        margin: 0 1.29rem 0 0;
        font-weight: ${(props) => props.theme.typography.fontWeightMedium};
        font-size: ${(props) => props.theme.typography.fontSizeDefault};
        border-bottom: 2px solid transparent;
        padding: 0.86rem 0.71rem;
        &-active {
          border-bottom: 2px solid ${(props) => props.theme.colors.blue};
        }
        &:nth-last-child(2) {
          margin: 0 0 0 auto !important;
        }
      }
    }
    &::before {
      border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
    }
    .ant-tabs-tab-disabled {
      .ant-tabs-tab-btn {
        pointer-events: none;
      }
    }
  }

  .ant-select-clear {
    right: 2.7rem;
  }
  .ant-tabs {
    overflow: inherit;
  }

  .ant-upload-list-item {
    margin-top: 0;
  }
  & > .ant-collapse-body {
    padding: 0;
    border: 1px solid red;
  }
`;
const RecipientsTabPlayers = styled.div`
  display: flex;
  flex-direction: column;

  > .ant-checkbox-wrapper {
    display: inline-flex;
    flex-direction: row-reverse;
    align-items: center;
    width: 7.5rem;
    margin-bottom: 1.14rem;
    margin-left: auto;

    @media (max-width: 1366px) {
      width: 100%;
    }
  }

  .ant-select {
    width: 100%;
    > div {
      padding-right: 0;
    }
  }
`;
const ComposeTemplateNavBar = styled.div`
  display: flex;
  justify-content: flex-end;
  & .ant-tabs-nav {
    width: 100%;
    margin-bottom: 0;
    & > div {
      display: flex;
      .ant-tabs-tab {
        font-weight: ${(props) => props.theme.typography.fontWeightMedium};
        font-size: ${(props) => props.theme.typography.fontSizeDefault};
        padding: 0.86rem 1.71rem;
        &-active {
          border-bottom: 2px solid ${(props) => props.theme.colors.blue};
        }
        &:last-child {
          margin-left: auto;
          padding-right: 0;
        }
      }
    }
    &::before {
      border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
    }
  }
  & > .ant-radio-group {
    overflow: auto;
  }
`;

const StylesFileInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.71rem;
  ${truncate()}
  
  ${StyledTruncate} {
    display: inline-block;
  }
  > span {
    margin-left: auto;
    font-size: 0.85rem;
    white-space: nowrap;

    &:not(:last-child) {
      margin-right: 0.71rem;
    }
  }
`;
const PreviewAttachmentsList = styled.div`
  ${StylesFileInfo} {
    border-bottom: 2px solid ${(props) => props.theme.colors.blue};
    padding-top: 0;
    padding-bottom: 0.57rem;
    margin-top: 1.14rem;
  }
`;
const StyledRecipientsSearch = styled.div`
  margin-bottom: 1.71rem;
`;

const StyledChannelsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  .ant-table-wrapper {
    margin-bottom: 16px;
    .ant-table-fixed-header {
      .ant-table-body {
        padding-right: 6px;
        margin-right: -11px;
        overflow: overlay !important;
      }
    }
  }
  .ant-table {
    & .ant-table-tbody > tr > td,
    & .ant-table-tbody > tr:last-child:not(.ant-table-expanded-row):not(.partners-brands-row):not(.permission-row) > td {
      border-bottom: none !important;
      background-color: ${(props) => props.theme.colors.secondaryBg} !important;
    }

    .ant-table-body {
      .ant-table-tbody > tr {
        > td {
          label {
            width: 1.3rem;
          }
        }
      }
    }
    &-container {
      background: ${(props) => props.theme.colors.secondaryBg};
    }
  }
  .ant-table .ant-table-tbody > tr:hover,
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${(props) => props.theme.colors.secondaryBg}!important;
  }
  .ant-btn:not(.ant-btn-icon-only) {
    align-self: flex-end;
    padding: 0 2.3rem;
    line-height: 2rem;
  }
  ${StyledButtonBlue} {
    margin: 1.71rem 0 0 auto;
  }
  .ant-checkbox {
    margin-right: 0.71rem;
  }
`;

const StyledChannel = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1px;
  height: 2rem;
  & > span {
    line-height: 1;
    margin-left: 0.85rem;
  }
`;

const StyledLanguageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.71rem;
`;
const StyledFlagLanguage = styled.div`
  margin-right: 1.14rem;
`;

const StyledFlagCompose = styled.div`
  margin-right: 8px;
`;
const StyledFlagViewAll = styled(StyledFlag)`
  display: inline-block !important;
`;

const ComposeTemplateLayout = styled.div`
  background-color: ${(props) => props.theme.colors.secondaryBg};
  flex-grow: 1;
  box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
  position: relative;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  display: flex;
  flex-direction: column;
  &:first-child .ant-radio-group {
    margin-bottom: 2.86rem;
  }
`;
const ComposeCard = styled.div`
  height: 100%;
  min-height: 12.1rem;
  cursor: pointer;
  padding: 2.29rem;
  box-shadow: ${() => getBoxShadow('#000000')}10;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder}60;
  background: ${(props) => props.theme.colors.mainCardBg};
  display: flex;
  align-items: flex-start;
  transition: 0.2s box-shadow ease-in-out;
  &:hover {
    box-shadow: ${() => getBoxShadow('#000000')}35;
  }
  .ant-radio-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 1.29rem;
    > span:last-child {
      font-size: ${(props) => props.theme.typography.fontSizeMiddle};
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
      color: ${(props) => props.theme.colors.blue};
      text-transform: uppercase;
    }
  }
`;
const ComposeCardContent = styled.div`
  margin: 0.7rem 0 0 1.14rem;
  > span {
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    color: ${(props) => props.theme.colors.primaryText};
    line-height: 1.71;
  }
`;

const ButtonDirectionWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: auto 0 2.28rem;
  .ant-btn:first-child {
    margin-right: 1.71rem;
  }
`;
const StyledComposeTemplateType = styled.div`
  margin-top: 5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledPreviewTemplate = styled(StyledButtonBlue)`
  &.ant-btn {
    color: ${(props) => props.theme.colors.blue};
    background-color: ${(props) => props.theme.colors.white};
    margin-bottom: 0.57rem;

    &:hover {
      background-color: ${(props) => props.theme.colors.hover};
    }
  }
`;

const TemplateCard = styled.div<TemplateCardProps>`
  position: relative;
  box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  transition: 0.3s all;
  cursor: pointer;
  overflow: hidden;

  ${(props) => props.$isSelected
    && css`
      box-shadow: 0 0 8px ${props.theme.colors.black}40;
      border-color: ${props.theme.colors.blue}90;
    `};

  &:hover {
    box-shadow: 0 0 8px ${(props) => props.theme.colors.black}40;
    border-color: ${(props) => props.theme.colors.blue}90;
    transform: scale(0.98);
  }
  > div:first-child {
    padding: 1.14rem 1.14rem 0.57rem;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
    background: ${(props) => props.theme.colors.mainCardBg};
    height: 100%;
    transition: 0.2s box-shadow ease-in-out;
    &:hover {
      box-shadow: ${() => getBoxShadow('#000000')}35;
    }
    & > ${StyledSvgIcon} {
      position: absolute;
      z-index: 9;
      right: 8px;
      top: 8px;
      background: ${(props) => props.theme.colors.blue};
      padding: 4px;
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusCircle};
    }
  }
  > a {
    margin: 0.75rem 1.14rem 3.36rem;
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    color: ${(props) => props.theme.colors.primaryText};
    display: inline-block;
    max-width: 90%;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const TemplateCardDeleted = styled.div`
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  padding: 2.85rem;
  gap: 1.2rem;
  height: 17.4rem;
  background-color: ${(props) => props.theme.colors.darkColor};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};

  p {
    color: ${(props) => props.theme.colors.white};
    line-height: 2rem;
    text-align: center;
  }
`;

const StyledSelectedTemplateName = styled.h3<StyledSelectedTemplateNameProps>`
  display: flex;
  gap: 0.4rem;
  margin: 1.71rem 0 0.57rem;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  span {
    color: ${(props) => (props.$isInvalid ? props.theme.colors.danger : props.theme.colors.blue)};
  }
`;

const CardFooter = styled.div`
  padding: 0.3rem 0 0.87rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const CardIconWrapper = styled.div`
  display: flex;
  > div {
    margin-right: 1rem;
  }
`;
const CollapseTitle = styled.div`
  text-transform: uppercase;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  margin-right: 1.14rem;
  color: ${(props) => props.theme.colors.primaryText};
  height: 1.14rem;
  line-height: 1.14rem;
`;

const PopupWrapperForSaveAction = styled.div<PopupWrapperForSaveActionProps>`
  display: flex;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  width: ${(props) => (props.$smallWidth ? ' 8.6rem' : '9.3rem')};
  align-items: center;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
  position: absolute;
  height: 2.57rem;
  .ant-dropdown-menu {
    width: 100%;
    height: 100%;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    box-shadow: none;
    padding: 0;
    &-item {
      display: flex;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0 1.14rem;
      align-items: center;
      > span {
        display: inline-block;
        width: 100%;
        text-align: center;
        font-size: ${(props) => props.theme.typography.fontSizeDefault};
        height: 2.57rem;
        line-height: 2.57rem;
      }
      &:hover {
        color: ${(props) => props.theme.colors.blue};
      }
    }

    &-item:active,
    &-submenu-title:active,
    &-item:hover,
    &-item-active,
    &:not(.ant-menu-inline) &-submenu-open,
    &-submenu-active,
    &-submenu-title:hover,
    &:not(.ant-menu-horizontal) .ant-menu-item-selected {
      background-color: ${(props) => props.theme.colors.hoverBg};
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    }
  }
`;

const SystemTemplatesDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  padding: 2.85rem 2.85rem 0;
  > div {
    word-break: break-word;
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    color: ${(props) => props.theme.colors.primaryText};
    text-transform: uppercase;
  }
  > .ant-divider.ant-divider-horizontal {
    margin-top: 1.14rem;
  }
  > h3 {
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    text-transform: uppercase;
  }
`;
const PreviewTotalInfo = styled.div`
  padding: 0 1.71rem;

  > div {
    padding-top: 2.28rem;
    p {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: ${(props) => props.theme.typography.fontSizeDefault};
      &:not(:last-child) {
        padding-bottom: 1.14rem;
      }
    }
  }
`;
const PreviewTotalInfoWrapper = styled(PreviewTotalInfo)<PreviewTotalInfoWrapperProps>`
  padding: 0 4rem;
  @media (max-width: 1366px) {
    padding: 0 2rem;
  }
  > div {
    border-bottom: ${(props) => (props.$noBorder ? 'none' : `1px solid ${props.theme.colors.secondaryBorder}`)};
    border-radius: 0;
    padding-bottom: 1.71rem;
    margin-top: 0;
    padding-top: 0;
    .ant-table-body {
      overflow-y: auto !important;
    }
    p {
      font-weight: ${(props) => props.theme.typography.fontWeightRegular};
    }
  }
`;
const StyledPreviewContentBody = styled.div`
  padding: 0 1.4rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
`;
const StyledPreviewContent = styled(StyledPreviewContentBody)`
  padding: 0;
  background: transparent;
  margin-top: 1.71rem !important;
`;

const PreviewContentTitle = styled.h3<PreviewContentTitleProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.71rem;
  font-size: ${(props) => (!props.$isRegular ? props.theme.typography.fontSizeMiddle : props.theme.typography.fontSizeDefault)};
  font-weight: ${(props) => (!props.$isRegular ? props.theme.typography.fontWeightMedium : 'unset')};
  padding-top: ${(props) => (!props.$isRegular ? '1.71rem' : '1.4rem')};
  text-transform: ${(props) => (!props.$isRegular ? 'uppercase' : 'unset')};
`;
const InvalidCount = styled.div`
  .ant-table .ant-table-tbody > tr.invalid-players-row > td {
    padding: 1rem 0.3rem !important;
    border-bottom: 1px solid ${(props) => props.theme.colors.primaryBorder};
  }
`;

const RecipientsCustomTableStyle = styled.div<RecipientsCustomTableStyleProps>`
  margin-top: 1.71rem;
  border-top: none;
  .ant-table table {
    table-layout: auto !important;
    .ant-table-tbody {
      tr {
        td {
          vertical-align: middle;
          height: 3.43rem;
        }
      }
    }
  }

  .ant-table .ant-table-container::after {
    display: none;
  }
`;

const ActionsStyles = styled.div`
  .tox-statusbar {
    display: none !important;
  }
`;
const MessageEditor = styled.div`
  margin: 1rem 0.71rem 0.71rem 2.29rem;
  position: relative;
`;
const CloseMessageEditorIcon = styled.div`
  position: absolute;
  z-index: 2;
  padding: 1rem 1.5rem;
  top: 0;
  right: 0;
  > div {
    &:hover {
      cursor: pointer;
    }
  }
`;
const ReplayButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 0.71rem;
  padding: 0.57rem 0;

  > span {
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
  }
  > button:first-of-type {
    margin: 0 2.29rem 0 1.14rem;
  }
`;
const ConversationLists = styled.div`
  max-height: 538px;
  overflow-y: auto;
`;
const ConversationListStyles = styled.div<ConversationListStylesProps>`
  background-color: ${(props) => (props.$isSenderUser ? `${props.$isMeRecalled ? `${props.theme.colors.blue}10` : `${props.theme.colors.blue}20`}` : `${props.theme.colors.success}10`)};
  margin: ${(props) => (props.$isSenderUser ? '0.57rem 0.71rem 0.57rem 9.71rem' : '0.57rem 6.43rem 0.57rem 2.29rem')};
  cursor: ${(props) => (props.$isMeRecalled ? 'not-allowed' : '')};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  padding: 1.14rem 1.71rem;
  width: auto;
  max-width: 100%;
  text-align: justify;
  white-space: break-spaces;

  ${media.sm`
    margin: ${(props) => (props.$isSenderUser ? '0.57rem 0 0.57rem 1.2rem' : '0.57rem 1.2rem 0.57rem 0')};
    padding: 1rem 1.14rem;
  `}

  p {
    word-break: break-word;
    margin-bottom: 1.14rem;
    em {
      font-style: italic;
    }
  }
  ul,
  ol {
    display: block;
    margin-bottom: 16px;
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style: decimal;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: #222f3e;
    font-style: normal;
    font-weight: bold;
    letter-spacing: normal;
    margin-bottom: 1.14rem;
    margin-top: 2rem;
    text-transform: none;
  }
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.17em;
  }
  h4 {
    font-size: 1em;
  }
  h5 {
    font-size: 0.83em;
  }
  h6 {
    font-size: 0.67em;
  }
  strong {
    font-weight: bold !important;
  }
  span,
  strong {
    display: inline !important;
  }
  pre {
    display: block;
    font-family: monospace;
    white-space: pre;
    margin: 1em 0;
  }
`;
const ConversationMessageContent = styled.div<ConversationMessageContentProps>`
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  opacity: ${(props) => (props.$isMeRecalled ? 0.4 : 1)};
`;
const RecallAndSeenIconsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  + div * {
    :not(span) {
      background-color: inherit !important;
      width: inherit !important;
      word-break: break-word;
    }
  }
`;
const RecallIconWrapper = styled.div`
  margin-right: 1rem;
`;
const ConversationTitle = styled.div`
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  margin-bottom: 0.57rem;
`;
const ConversationContent = styled.div<ConversationContentProps>`
  display: flex;
  justify-content: space-between;
  opacity: ${(props) => (props.$isMeRecalled ? 0.4 : 1)};

  > span {
    font-size: ${(props) => props.theme.typography.fontSizeSmall};
    color: ${(props) => props.theme.colors.primaryText};
  }
`;

const CollapseIconWrapper = styled.div`
  &.ant-collapse-arrow {
    display: flex;
  }
`;

const StyledReportsBackWrapper = styled.div`
  background: ${(props) => props.theme.colors.secondaryBg};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  flex-grow: 1;
`;
const StyledBackContent = styled.div`
  padding: 2rem 2.85rem;
  background: ${(props) => props.theme.colors.secondaryBg};
  height: 100%;

  ${media.sm`
    padding: 1.4rem 1.4rem 1.8rem;
  `}
`;

const StyledCancelIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 1.43rem;
  cursor: pointer;
`;
const StyledBackForPreviewContent = styled.div`
  position: relative;
  top: 0;
  height: 4rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.primaryBorder};
  padding-left: 2.36rem;
  cursor: pointer;
  > strong {
    margin-left: 0.86rem;
    color: ${(props) => props.theme.colors.primaryText};
  }
`;
const StyledRecipientsId = styled.div`
  display: flex;
  align-items: center;
`;
const RecipientsInfoIconWrapper = styled.div`
  margin-left: 0.57rem;
`;
const TemplateContentWrapper = styled.div`
  height: 100%;
  min-height: 30rem;
  margin-top: 1.14rem;
  position: relative;
  display: flex;
  flex-direction: column;
  .ant-row {
    flex-grow: 1;
  }
`;
const TemplateEmptyContentWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const StyledMessageContentSwitcher = styled.div`
  display: flex;
  justify-content: flex-end;
  .ant-form-item {
    flex-direction: row;
  }
  .ant-form-item-label {
    margin-right: 0.57rem;
    label {
      height: 1.5rem;
    }
  }
`;

const StyledTextareaControls = styled.div`
  position: relative;
  padding: 0.71rem 1.71rem;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};

  &:before {
    content: '';
    position: absolute;
    width: calc(100% - (2 * 1.71rem));
    top: 0;
    left: 1.71rem;
    height: 1px;
    background-color: ${(props) => props.theme.colors.secondaryBorder};
  }
`;
const StyledTextareaTag = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: 0.71rem;
  padding: 0.28rem 0.5rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusDefault};
  cursor: pointer;
  transition: 0.3s background-color;

  &:hover,
  &.ant-popover-open {
    background-color: #cce2fa;
  }

  svg path {
    fill: ${(props) => props.theme.colors.black};
  }
`;
const StyledTextareaWrapper = styled.div`
  &,
  .ant-input-textarea-affix-wrapper {
    border: none;
    box-shadow: none;

    textarea.ant-input {
      border-radius: ${(props) => `0 0 ${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle}`};
      border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
      box-shadow: inset 0 0 6px 0 ${(props) => props.theme.colors.secondaryShadow};
      color: ${(props) => props.theme.colors.primaryText};
      margin-bottom: 0.57rem;
      padding: 1.4rem 1.14rem;

      &:focus {
        box-shadow: none;
      }
    }
    .ant-input-data-count {
      color: ${(props) => props.theme.colors.primaryText}80;
    }
  }
`;
const StyledTextareaPreview = styled.div`
  flex-grow: 1;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  padding: 1.71rem;
  word-break: break-word;
`;
const StyledTextareaExtra = styled.div`
  margin: 2.57rem 0;
  color: ${(props) => props.theme.colors.primaryText}80;
  text-align: right;
  line-height: 1.71rem;
`;
const StyledTruncateFileName = styled(StyledTruncate)<StyledTruncateFileNameProps>`
  color: ${(props) => props.theme.colors.blue};
  cursor: pointer;
  font-size: 0.85rem;
  span {
    display: ${(props) => (props.$isPreview ? 'none' : 'initial')};
  }
`;
const StyledRemoveIconInEditMode = styled.div`
  margin-left: 1.14rem;
`;
const StyledPreferenceSymbol = styled.div`
  position: relative;
  span {
    width: 5px;
    height: 5px;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusCircle};
    background: ${(props) => props.theme.colors.blue};
    display: block;
    position: absolute;
    top: 22px;
    left: 80px;
  }
`;

const StyledContentPaddings = styled.div<StyledContentPaddingsProps>`
  padding: ${(props) => (props.$isPaddingTop ? '3.57rem 4rem 0' : '0 2.57rem')};
  height: ${(props) => (props.$isFullHeight ? '100%' : 'auto')};
  ${media.sm`
      padding: ${(props) => (props.$isPaddingTop ? '3.57rem 1.14rem 0' : '0 2.57rem')};
    `}
`;

const StyledChannelsIconsWrapper = styled.div<StyledChannelsIconsWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  svg:hover {
    path {
      fill: ${(props) => props.theme.colors.hover};
    }
  }
  > div:not(:last-child) {
    border-right: ${(props) => (props.$length ? `1px solid ${props.theme.colors.primaryBorder}` : 'none')};
    padding-right: 0.57rem;
    margin-right: 0.8rem;
  }
`;
const StyledTemplateIcon = styled.div`
  background-color: ${(props) => props.theme.colors.tableTrEvenColor};
  > div {
    width: 4.5rem;
    height: 4.5rem;
  }
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusCircle};
`;

const StyledNotifyPlayersCount = styled.span<StyledNotifyPlayersCountProps>`
  padding: 0.28rem 0.57rem;
  margin-left: 0.57rem !important;
  background: ${(props) => props.theme.colors.primaryBg};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  font-size: 1rem;
  color: ${(props) => props.theme.colors[props.$color]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const StyledPushNotificationComposeWrapper = styled.div`
  width: calc(100% - 25.2rem);
  ${media.lg`
    width: 100%; 
  `}
  .ant-tabs {
    padding: 0 0.71rem 0 2.57rem;

    ${media.sm`
      padding: 0 1.14rem;
    `}
  }
`;

const StyledEmojiBarToggleButton = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: 0.71rem;
  cursor: pointer;
`;

const StyledPushContentAndPreview = styled.div`
  display: flex;
  justify-content: space-between;
  ${media.lg`
   flex-direction: column;
  `}
`;
const StyledDevicePreviewWrapper = styled.div`
  width: 25.2rem;
  padding: 0 2.28rem;
  ${media.lg`
   margin: 0 auto;
  `}
  h5 {
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    display: inline-block;
    margin-bottom: 0.71rem;
  }

  .ant-radio-group.ant-radio-group-outline {
    margin: 1.71rem 0;
    width: 100%;
    padding: 0;
    min-height: unset;
  }
`;
const StyledDevicePreviewRadioList = styled(StyledFileRadioList)`
  li {
    flex: 0 0 50% !important;
    margin: 0 !important;
  }
`;
const StyledDeviceItemWrapper = styled.div`
  margin-bottom: 2rem;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    display: inline-block;
    width: calc(100% + 22px);
    left: -11px;
    border-bottom: 1px solid ${(props) => props.theme.colors.black}30;
  }

  h6 {
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    margin-bottom: 1.14rem;
    text-align: center;
  }
`;
const StyledDeviceItem = styled.div`
  overflow: hidden;
  position: relative;

  & > img {
    display: flex;
    width: 100%;
    height: auto;
  }
`;
const StyledPushPreviewItem = styled.div<StyledPushPreviewItemProps>`
  position: absolute;
  top: 3.28rem;
  background-color: ${(props) => props.theme.colors.secondaryBg};

  border-radius: ${(props) => (props.$isApple ? '16px' : props.theme.customBorderRadius.borderRadiusSmall)};
  padding: ${(props) => (props.$isApple ? '0.86rem 1.14rem 0.86rem 0.86rem' : '1.14rem')};
  width: ${(props) => (props.$isApple ? 'calc(100% - 3.42rem)' : 'calc(100% - 2.28rem)')};
  left: ${(props) => (props.$isApple ? '1.71rem' : '1.14rem')};
  transition: 0.3s all;

  svg {
    width: ${(props) => (props.$isApple ? '2.28rem' : '1rem')};
    height: ${(props) => (props.$isApple ? '2.28rem' : '1rem')};
  }

  ${(props) => props.$isExpanded
    && props.$isApple
    && `
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}
`;
const StyledPushPreviewInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.57rem;
`;
const StyledPushPreviewInnerRight = styled.div`
  width: 100%;
`;
const StyledPushPreviewTitle = styled.h3<StyledPushPreviewTitleProps>`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.$isExpanded ? 4 : 1)};
  line-clamp: ${(props) => (props.$isExpanded ? 4 : 1)};
  -webkit-box-orient: vertical;
  white-space: normal;

  font-size: 1rem;
  line-height: 1.2;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  margin-bottom: 0.28rem;
  transition: 0.3s all;
`;
const StyledPushPreviewContent = styled.div<StyledPushPreviewContentProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.87rem;

  img {
    display: ${(props) => (props.$isExpanded ? 'none' : 'inline-block')};
    width: ${(props) => (props.$isApple ? '32px' : '20px')};
    height: ${(props) => (props.$isApple ? '32px' : '20px')};
    border-radius: 6px;
  }
`;
const StyledPushPreviewMessage = styled.p`
  font-size: 0.71rem;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  white-space: normal;
`;
const StyledPushExpandedAttachment = styled.img<StyledPushExpandedAttachmentProps>`
  display: block;
  transition: 0.3s all;
  object-fit: cover;
  opacity: ${(props) => (props.$isExpanded ? 1 : 0)};
  width: 100%;

  ${(props) => (props.$isExpanded && props.$isApple
    ? `
    position: absolute;
    top: 100%;
    left: 0;
    border-radius: 0 0 16px 16px;
    width: 240px;
    height: 120px;
    max-height: ${props.$isExpanded ? '100px' : 0};
  `
    : `
    border-radius: ${props.theme.customBorderRadius.borderRadiusMiddle};
    margin-top: ${props.$isExpanded ? '0.57rem' : 0};
    width: 220px;
    height: 90px;
    max-height: ${props.$isExpanded ? '100px' : 0};
  `)}
`;

const StyledEmojiPickerWrapper = styled.div`
  aside.EmojiPickerReact {
    --epr-emoji-size: 26px;
    --epr-category-label-height: 20px;
    .epr-emoji-category {
      margin-bottom: 0.57rem;
      .epr-emoji-category-label {
        font-size: 1rem;
        font-weight: normal;
      }
    }
  }
`;

const StyledWhatsAppEditorWrapper = styled.div`
  textarea.ant-input {
    margin-bottom: 0;
    border-bottom-right-radius: 0;
  }
  ${StyledMessageSubject} {
    display: flex;
    align-items: center;
    p {
        white-space: nowrap;
    }
    .ant-select {
      max-width: 20rem;
    }
  }
`;

const StyledWhatsAppTagsWrapper = styled.div`
  padding: 1.14rem;
  height: 100%;
  border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-bottom-right-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  .ant-table {
    margin-top: 1rem;
  }
`;

export {
  StyledComposeTemplateWrapper,
  StyledComposeTemplateLeft,
  StyledComposeTemplateRight,
  StyledComposeInnerTabsWrapper,
  StyledComposeButtons,
  StyledMessageSubject,
  StyledRecipientsWrapper,
  StyledSelectedPlayers,
  StyledRecipientsSearch,
  StyledChannelsWrapper,
  StyledChannel,
  StyledLanguageWrapper,
  StyledFlagLanguage,
  StyledFlagCompose,
  ComposeTemplateLayout,
  ComposeCard,
  ComposeCardContent,
  ButtonDirectionWrapper,
  StyledPreviewTemplate,
  TemplateCard,
  TemplateCardDeleted,
  StyledSelectedTemplateName,
  CardFooter,
  CardIconWrapper,
  CollapseTitle,
  ComposeTemplateNavBar,
  RecipientsTabPlayers,
  MessageSwitcherWrapper,
  PopupWrapperForSaveAction,
  PreviewTotalInfo,
  RecipientsCustomTableStyle,
  InvalidCount,
  PreviewContentTitle,
  ActionsStyles,
  ReplayButtons,
  ConversationLists,
  ConversationListStyles,
  ConversationTitle,
  ConversationContent,
  MessageEditor,
  CloseMessageEditorIcon,
  StylesFileInfo,
  ConversationMessageContent,
  RecallAndSeenIconsWrapper,
  ComposeTextWrapper,
  CreateMessageContentWrapper,
  SystemTemplatesDescriptionWrapper,
  CollapseIconWrapper,
  StyledReportsBackWrapper,
  StyledBackContent,
  StyledCancelIcon,
  StyledBackForPreviewContent,
  StyledPreviewMessageSubject,
  StyledPreviewContentBody,
  RecallIconWrapper,
  StyledRecipientsId,
  RecipientsInfoIconWrapper,
  TemplateContentWrapper,
  TemplateEmptyContentWrapper,
  PreviewMessageSubjectWrapper,
  StyledMessageContentSwitcher,
  StyledTextareaControls,
  StyledTextareaTag,
  StyledTextareaWrapper,
  StyledTextareaPreview,
  StyledTextareaExtra,
  StyledComposeTemplateType,
  StyledTruncateFileName,
  PreviewAttachmentsList,
  StyledRemoveIconInEditMode,
  StyledComposeTemplateMargin,
  StyledChannelsIconsWrapper,
  StyledPreferenceSymbol,
  StyledFlagViewAll,
  StyledContentPaddings,
  StyledTemplateIcon,
  StyledPreviewContent,
  PreviewTotalInfoWrapper,
  StyledNotifyPlayersCount,
  StyledPushNotificationComposeWrapper,
  StyledEmojiBarToggleButton,
  StyledPushContentAndPreview,
  StyledDevicePreviewWrapper,
  StyledDevicePreviewRadioList,
  StyledDeviceItemWrapper,
  StyledDeviceItem,
  StyledPushPreviewItem,
  StyledPushPreviewInner,
  StyledPushPreviewInnerRight,
  StyledPushPreviewTitle,
  StyledPushPreviewContent,
  StyledPushPreviewMessage,
  StyledPushExpandedAttachment,
  StyledEmojiPickerWrapper,
  StyledWhatsAppEditorWrapper,
  StyledWhatsAppTagsWrapper,
};
